import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { AppLayoutStateService } from '@app/app-layout/app-layout.state-service';
import { NavigationLink, TopNavigationLink } from '@app/app-layout/header/product-navigation/navigation-bar/navigation-bar';
import { employeeHubRoutes } from '@app/employee-hub/employee-hub-routes';
import { HolidayApproverSummary } from '@app/employee-hub/holidays/holiday-approvals';
import { FeatureFlagService, FeatureFlagsServiceInjectionToken } from '@feature-flags/feature-flag.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActiveSecurityContextStateService } from '@security/active-security/active-security-context.state-service';
import { BehaviorSubject, take } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-employee-hub-navigation',
  templateUrl: './employee-hub-navigation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmployeeHubNavigationComponent {
  topNavigationLinks$ = new BehaviorSubject<TopNavigationLink[]>([]);

  constructor(
    public activeSecurity: ActiveSecurityContextStateService,
    appLayoutService: AppLayoutStateService,
    @Inject(FeatureFlagsServiceInjectionToken) public featureFlagService: FeatureFlagService
  ) {
    appLayoutService.holidayApproverSummary$.pipe(untilDestroyed(this)).subscribe(this.createEmployeeHubLinks);
  }

  createEmployeeHubLinks = (approverSummary: HolidayApproverSummary) => {
    this.featureFlagService
      .getAllValues$()
      .pipe(take(1))
      .subscribe((featureFlags) => {
        const topNavigationLinks: TopNavigationLink[] = [];

        topNavigationLinks.push({
          label: 'Dashboard',
          route: 'employee-hub/dashboard',
          feature: 'EmployeeHubDashboard'
        });

        if (this.activeSecurity.hasEmploymentFeaturePackAuthorityTo('AccessEmployeeHubHolidays'))
          topNavigationLinks.push({
            label: 'Holidays',
            route: 'employee-hub/my-holidays',
            feature: 'Holidays'
          });

        if (this.activeSecurity.hasEmploymentFeaturePackAuthorityTo('AccessEmployeeHubDocuments'))
          topNavigationLinks.push({
            label: 'Payslips',
            route: 'employee-hub/payslips',
            feature: 'Payslips'
          });

        if (this.activeSecurity.hasEmploymentFeaturePackAuthorityTo('AccessEmployeeHubDocuments'))
          topNavigationLinks.push({
            label: 'Documents',
            route: 'employee-hub/documents',
            feature: 'HubDocuments'
          });

        if (this.activeSecurity.hasEmploymentFeaturePackAuthorityTo('AccessEmployeeHubPersonalDetails'))
          topNavigationLinks.push({
            label: 'Profile',
            route: 'employee-hub/my-profile',
            feature: 'MyProfile'
          });

        if (approverSummary.isHolidayApprover && this.activeSecurity.hasEmploymentFeaturePackAuthorityTo('AccessEmployeeHubHolidays')) {
          const approvalSectionLinks = <NavigationLink[]>[
            {
              label: 'Requests',
              route: employeeHubRoutes.holidayApprovalsRequests,
              counter: approverSummary.openHolidayRequestCount
            }
          ];

          approvalSectionLinks.push({
            label: 'Calendar',
            route: employeeHubRoutes.holidayApprovalsCalendar
          });

          approvalSectionLinks.push({
            label: 'Entitlements',
            route: employeeHubRoutes.holidayApprovalsEntitlements
          });

          approvalSectionLinks.push({
            label: 'History',
            route: employeeHubRoutes.holidayApprovalsHistory
          });

          const approvalsLink = <TopNavigationLink>{
            label: 'Approvals',
            feature: 'HolidayApprovals',
            counter: approverSummary.openHolidayRequestCount,
            sectionLinks: approvalSectionLinks
          };
          topNavigationLinks.push(approvalsLink);
        }

        this.topNavigationLinks$.next(topNavigationLinks);
      });
  };
}
